import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  CheckSquare, Plus, Trash2, Circle, Clock, CheckCircle2,
  Sparkles, Loader2, User, Calendar, Link2, ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_TYPES = [
  { id: "general", label: "Ogolne" },
  { id: "content", label: "Tresc" },
  { id: "seo", label: "SEO" },
  { id: "technical", label: "Techniczne" },
  { id: "review", label: "Review" },
];

const MODULES = [
  "Dashboard", "Sites", "Generator", "HTML Generator", "RSS", "Monitor",
  "Trends", "Rewrite", "Mass Gen", "Affiliates", "WooCommerce",
  "Social Media", "Reports", "Comments", "Forums", "Tasks"
];

const priorityColor = {
  low: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  medium: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  high: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  critical: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const statusIcon = {
  todo: <Circle className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />,
  in_progress: <Clock className="w-4 h-4 text-blue-500" strokeWidth={1.5} />,
  done: <CheckCircle2 className="w-4 h-4 text-emerald-500" strokeWidth={1.5} />,
};

export default function Tasks() {
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [suggestingId, setSuggestingId] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", task_type: "general", priority: "medium",
    status: "todo", module: "", assigned_to: "", due_date: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, statsRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/tasks/stats`),
      ]);
      setTasks(tasksRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ title: "", description: "", task_type: "general", priority: "medium", status: "todo", module: "", assigned_to: "", due_date: "" });
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    try {
      await axios.post(`${API}/tasks`, {
        ...form, module: form.module || null, assigned_to: form.assigned_to || null, due_date: form.due_date || null
      });
      toast.success(t("common.success"));
      fetchData(); setDialogOpen(false); resetForm();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, { ...task, status: newStatus });
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleSuggest = async (taskId) => {
    setSuggestingId(taskId);
    try {
      const res = await axios.post(`${API}/tasks/${taskId}/suggest`);
      toast.success(t("tasksAdv.suggested"));
      fetchData();
      if (selectedTask?.id === taskId) setSelectedTask(t2 => ({ ...t2, ai_suggestions: res.data }));
    } catch (err) { toast.error(t("common.error")); }
    finally { setSuggestingId(null); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/tasks/${id}`);
      if (selectedTask?.id === id) setSelectedTask(null);
      toast.success(t("common.success")); fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const grouped = {
    todo: tasks.filter(t2 => t2.status === "todo"),
    in_progress: tasks.filter(t2 => t2.status === "in_progress"),
    done: tasks.filter(t2 => t2.status === "done"),
  };

  const isOverdue = (task) => {
    if (!task.due_date) return false;
    return new Date(task.due_date) < new Date() && task.status !== "done";
  };

  return (
    <div data-testid="tasks-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("tasks.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("tasks.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-task-btn" className="rounded-lg gap-2">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("tasks.addTask")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{t("tasks.addTask")}</DialogTitle></DialogHeader>
            <div className="grid gap-3 py-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("tasks.taskTitle")}</Label>
                <Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("tasks.description")}</Label>
                <Textarea data-testid="task-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("tasksAdv.type")}</Label>
                  <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_TYPES.map(tt => <SelectItem key={tt.id} value={tt.id} className="text-xs">{tt.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("tasks.priority")}</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low" className="text-xs">{t("tasks.priorities.low")}</SelectItem>
                      <SelectItem value="medium" className="text-xs">{t("tasks.priorities.medium")}</SelectItem>
                      <SelectItem value="high" className="text-xs">{t("tasks.priorities.high")}</SelectItem>
                      <SelectItem value="critical" className="text-xs">{t("tasks.priorities.critical")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("tasks.module")}</Label>
                  <Select value={form.module || "none"} onValueChange={(v) => setForm({ ...form, module: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">—</SelectItem>
                      {MODULES.map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("tasksAdv.assignedTo")}</Label>
                  <Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} placeholder="Jan Kowalski" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("tasksAdv.dueDate")}</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>{t("tasks.cancel")}</Button>
              <Button data-testid="save-task-btn" onClick={handleSave}>{t("tasks.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: t("tasks.statuses.todo"), value: stats.todo, icon: Circle, color: "text-slate-500" },
            { label: t("tasks.statuses.in_progress"), value: stats.in_progress, icon: Clock, color: "text-blue-500" },
            { label: t("tasks.statuses.done"), value: stats.done, icon: CheckCircle2, color: "text-emerald-500" },
            { label: t("tasksAdv.totalTasks"), value: stats.total, icon: CheckSquare, color: "text-slate-400" },
          ].map((s, i) => (
            <Card key={i} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-3 flex items-center gap-3">
                <s.icon className={`w-5 h-5 ${s.color}`} strokeWidth={1.5} />
                <div>
                  <p className="text-xl font-bold font-heading text-foreground">{s.value}</p>
                  <p className="text-[10px] text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Kanban columns */}
        {Object.entries(grouped).map(([status, items]) => (
          <div key={status} className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              {statusIcon[status]}
              <h3 className="text-xs font-semibold text-foreground font-heading">{t(`tasks.statuses.${status}`)}</h3>
              <Badge variant="secondary" className="rounded-md text-[10px] ml-auto">{items.length}</Badge>
            </div>
            <ScrollArea className="max-h-[500px]">
              <div className="space-y-2">
                {items.map((task) => (
                  <Card key={task.id} data-testid={`task-card-${task.id}`}
                    className={`rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer ${
                      selectedTask?.id === task.id ? "border-primary shadow-md" : "bg-card"
                    } ${isOverdue(task) ? "border-red-300 dark:border-red-800" : ""}`}
                    onClick={() => setSelectedTask(task)}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-1.5">
                        <h4 className="text-xs font-semibold text-foreground leading-snug pr-2">{task.title}</h4>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDelete(task.id); }}
                          className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive">
                          <Trash2 className="w-2.5 h-2.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                      {task.description && <p className="text-[10px] text-muted-foreground line-clamp-2 mb-2">{task.description}</p>}
                      <div className="flex items-center gap-1 flex-wrap mb-2">
                        <Badge className={`rounded-md text-[9px] font-semibold border-0 ${priorityColor[task.priority] || ""}`}>
                          {t(`tasks.priorities.${task.priority}`)}
                        </Badge>
                        {task.module && <Badge variant="outline" className="rounded-md text-[9px]">{task.module}</Badge>}
                        {task.task_type && task.task_type !== "general" && (
                          <Badge variant="secondary" className="rounded-md text-[8px]">{task.task_type}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[9px] text-muted-foreground">
                        {task.assigned_to && <span className="flex items-center gap-0.5"><User className="w-2.5 h-2.5" />{task.assigned_to}</span>}
                        {task.due_date && (
                          <span className={`flex items-center gap-0.5 ${isOverdue(task) ? "text-red-500 font-semibold" : ""}`}>
                            <Calendar className="w-2.5 h-2.5" />{new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {status !== "done" && (
                        <div className="flex gap-1 mt-2">
                          {status === "todo" && (
                            <Button variant="outline" size="sm" className="h-6 text-[9px] rounded-md"
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(task, "in_progress"); }}>
                              {t("tasks.statuses.in_progress")}
                            </Button>
                          )}
                          <Button variant="outline" size="sm" className="h-6 text-[9px] rounded-md"
                            onClick={(e) => { e.stopPropagation(); handleStatusChange(task, "done"); }}>
                            {t("tasks.statuses.done")}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>
        ))}

        {/* Detail panel */}
        {selectedTask && (
          <div>
            <Card data-testid="task-detail" className="rounded-xl border shadow-sm bg-card sticky top-20">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-heading text-foreground">{selectedTask.title}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-1 space-y-3">
                {selectedTask.description && <p className="text-xs text-muted-foreground">{selectedTask.description}</p>}
                <div className="flex flex-wrap gap-1">
                  <Badge className={`text-[9px] border-0 rounded-md ${priorityColor[selectedTask.priority]}`}>{selectedTask.priority}</Badge>
                  {selectedTask.module && <Badge variant="outline" className="text-[9px] rounded-md">{selectedTask.module}</Badge>}
                  {selectedTask.task_type !== "general" && <Badge variant="secondary" className="text-[9px] rounded-md">{selectedTask.task_type}</Badge>}
                </div>
                {selectedTask.assigned_to && <p className="text-[10px] text-muted-foreground"><User className="w-3 h-3 inline mr-1" />{selectedTask.assigned_to}</p>}
                {selectedTask.due_date && <p className={`text-[10px] ${isOverdue(selectedTask) ? "text-red-500" : "text-muted-foreground"}`}><Calendar className="w-3 h-3 inline mr-1" />{new Date(selectedTask.due_date).toLocaleDateString()}</p>}

                {/* AI suggestions */}
                <Separator />
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-semibold text-foreground">{t("tasksAdv.aiSuggestions")}</p>
                  <Button size="sm" data-testid="suggest-task-btn" onClick={() => handleSuggest(selectedTask.id)}
                    disabled={suggestingId === selectedTask.id} className="h-6 text-[9px] gap-1 rounded-md">
                    {suggestingId === selectedTask.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
                    {t("tasksAdv.suggest")}
                  </Button>
                </div>
                {selectedTask.ai_suggestions && (
                  <div className="space-y-2">
                    {selectedTask.ai_suggestions.steps?.length > 0 && (
                      <div className="p-2 rounded-lg bg-muted/50">
                        <p className="text-[9px] uppercase font-semibold text-muted-foreground mb-1">{t("tasksAdv.steps")}</p>
                        {selectedTask.ai_suggestions.steps.map((s, i) => (
                          <p key={i} className="text-[10px] text-foreground py-0.5 pl-2 border-l-2 border-primary/40">{i+1}. {s}</p>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2 text-[9px]">
                      {selectedTask.ai_suggestions.estimated_hours && (
                        <Badge variant="secondary" className="text-[8px] rounded-md">{selectedTask.ai_suggestions.estimated_hours}h</Badge>
                      )}
                      {selectedTask.ai_suggestions.tips && (
                        <p className="text-muted-foreground">{selectedTask.ai_suggestions.tips}</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
